import { useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import { cn, Dropzone } from '@teach/ui'
import { UploadProgressOverlay } from '../shared/UploadProgressOverlay'
import {
  appendStyleReference,
  canAddStyleReference,
  removeStyleReferenceAt,
} from '../../lib/styleReferences'
import { useFileUpload } from '../shared/useFileUpload'
import { RemoveItemButton } from '../blocks/shared/RemoveItemButton'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  value: string[]
  disabled: boolean
  disabledHint?: string
  /** Резолвится успехом PUT — до него старый блоб трогать нельзя. */
  onChange: (next: string[]) => Promise<boolean>
}

// Сетка миниатюр по образцу слотов маскота: кнопка добавления в роли
// загрузчика, кнопка удаления на миниатюре. Черновик, а не чтение из пропа:
// `styleReferences` — полная замена на бэке, а обновлённый курс приезжает
// асинхронно (updateCourse только инвалидирует тег). Без черновика второе
// действие в окне рефетча считало бы от списка ДО первого PUT.
export const StyleReferencesGallery = ({ value, disabled, disabledHint, onChange }: Props) => {
  const m = useMessages(messages)
  const [items, setItems] = useState<string[]>(value)
  // Ref рядом с состоянием: `handleFile` продолжается ПОСЛЕ await, где
  // замыкание держит список на момент старта загрузки. Считав его оттуда,
  // мы вернули бы в PUT картинку, которую автор успел удалить, — и курс
  // сослался бы на уже стёртый из бакета блоб.
  const itemsRef = useRef(items)
  const { progress, upload, removeFile } = useFileUpload({
    compressionType: 'banner_compression',
  })
  const uploading = progress !== null
  const inputRef = useRef<HTMLInputElement>(null)

  const commit = async (next: string[]): Promise<boolean> => {
    itemsRef.current = next
    setItems(next)
    return onChange(next)
  }

  const handleFile = async (file: File) => {
    const url = await upload(file)
    if (!url) return
    const current = itemsRef.current
    // Место могло кончиться, пока файл летел: сохранять нечего, а свежий
    // блоб надо убрать за собой, иначе он осиротеет в бакете.
    if (!canAddStyleReference(current)) {
      removeFile(url)
      return
    }
    const saved = await commit(appendStyleReference(current, url))
    if (!saved) removeFile(url)
  }

  const handleRemove = async (index: number) => {
    const previous = itemsRef.current[index]
    const saved = await commit(removeStyleReferenceAt(itemsRef.current, index))
    if (previous && saved) removeFile(previous)
  }

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        disabled={disabled}
        aria-label={m.styleRefFile}
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (file) void handleFile(file)
        }}
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
        {items.map((url, index) => (
          <div
            key={url}
            className="group relative aspect-video overflow-hidden rounded-2xl border-2 border-dashed border-border"
          >
            <img src={url} alt="" className="h-full w-full object-cover" />
            {/* Во время загрузки корзина скрыта — тот же гвард, что у
                MascotSlot: удаление в этом окне разошлось бы с долетающей
                загрузкой. Подпись с номером: три одинаковых имени
                скринридер читает как одну кнопку. */}
            {disabled || uploading ? null : (
              <div className="absolute right-1 top-1 rounded-full bg-background/80 opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
                <RemoveItemButton
                  label={m.styleRefRemove(index + 1)}
                  onClick={() => void handleRemove(index)}
                />
              </div>
            )}
          </div>
        ))}
        {canAddStyleReference(items) ? (
          <div
            // Пока грузим, зона скрыта под оверлеем — пунктир держит сам контейнер.
            className={cn(
              'relative aspect-video rounded-2xl',
              uploading ? 'border-2 border-dashed border-border' : null,
            )}
            title={disabled ? disabledHint : undefined}
          >
            <UploadProgressOverlay progress={progress} />
            <Dropzone
              icon={Upload}
              aria-label={m.styleRefUpload}
              title={m.styleRefTitle}
              onClick={() => inputRef.current?.click()}
              disabled={disabled || uploading}
              className={cn(
                'flex h-full flex-col items-center justify-center !p-2',
                disabled ? 'opacity-40' : '',
                // Под оверлеем загрузки подпись зоны просвечивала бы сквозь него.
                uploading ? '[&>*]:invisible' : '',
              )}
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}
